import React, { useCallback, useEffect, useState } from 'react';
import type { InboundEmail } from '../../types/agent';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { EmailStatusChip, RunStatusChip, REJECT_REASONS, fmtDateTime, EmptyState, Spinner, card, input } from './agentUi';
import InboundEmailDetail from './InboundEmailDetail';

const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'queued,processing', label: 'In progress' },
  { key: 'completed', label: 'Handled' },
  { key: 'quarantined,rejected', label: 'Rejected / quarantined' },
  { key: 'failed', label: 'Failed' },
];

const InboundEmailList: React.FC<{ onOpenRun: (runId: string) => void; refreshKey?: number }> = ({ onOpenRun, refreshKey }) => {
  const [emails, setEmails] = useState<InboundEmail[] | null>(null);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await agentApi.inbox({ status, q: q.trim() || undefined });
      setEmails(r.emails);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the inbox'));
    }
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    const i = setInterval(load, 20000);
    return () => {
      clearTimeout(t);
      clearInterval(i);
    };
  }, [load, refreshKey]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatus(f.key)}
            className={`text-xs px-2.5 py-1 rounded-full border ${status === f.key ? 'border-primary text-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <input className={`${input} w-56`} placeholder="Search subject…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {emails === null ? (
        <div className="flex items-center gap-2 text-gray-500 py-10 justify-center"><Spinner /> Loading…</div>
      ) : emails.length === 0 ? (
        <EmptyState title="No email yet">Forward an email to the agent mailbox shown in Settings — it appears here within a minute.</EmptyState>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left font-medium px-3 py-2">Received</th>
                <th className="text-left font-medium px-3 py-2">From</th>
                <th className="text-left font-medium px-3 py-2">Subject</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-left font-medium px-3 py-2">Run</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {emails.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer" onClick={() => setOpenId(e.id)}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">{fmtDateTime(e.receivedAt || e.createdAt)}</td>
                  <td className="px-3 py-2 text-gray-800 dark:text-gray-100 max-w-[14rem] truncate">{e.fromName || e.fromAddress}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white max-w-[24rem]">
                    <div className="truncate">{e.subject || '(no subject)'}</div>
                    <div className="text-xs text-gray-500">
                      {e.attachmentCount ? `📎 ${e.attachmentCount}` : ''}
                      {e.pinnedIntent ? ` · ${e.pinnedIntent.replace(/_/g, ' ')}` : ''}
                      {e.simulated ? ' · simulated' : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <EmailStatusChip status={e.status} />
                    {e.rejectReason && ['rejected', 'quarantined'].includes(e.status) && <div className="text-[11px] text-gray-500 mt-0.5">{REJECT_REASONS[e.rejectReason] || e.rejectReason}</div>}
                  </td>
                  <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
                    {e.run ? (
                      <button className="text-primary hover:underline" onClick={() => onOpenRun(e.run!.id)}>
                        <RunStatusChip status={e.run.status} />
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId && <InboundEmailDetail emailId={openId} onClose={() => setOpenId(null)} onOpenRun={(id) => { setOpenId(null); onOpenRun(id); }} onChanged={load} />}
    </div>
  );
};

export default InboundEmailList;
